/**
 * 兑换码与奖励系统集成Hook
 * 处理兑换成功后的奖励弹窗显示和系统集成
 */

import log from "logbeacon";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RewardPopupCreateAble } from "../components/modal/RewardPopup/RewardPopup";
import { QUERY_KEYS } from "../config";
import { Reward } from "./useQueryRewards";

export interface RedeemCodeIntegration {
  triggerRewardPopup: (rewards: Reward[]) => Promise<void>;
  createMockRewardRecords: (
    rewards: Reward[],
    ownerId: string,
  ) => Promise<void>;
  refreshRewards: () => Promise<void>;
}

export const useRedeemCodeIntegration = (): RedeemCodeIntegration => {
  const queryClient = useQueryClient();

  const triggerRewardPopup = useCallback(
    async (rewards: Reward[]): Promise<void> => {
      try {
        log.info("[RedeemCodeIntegration] Triggering reward popup", {
          rewards,
        });

        await RewardPopupCreateAble({
          rewards,
          modalProps: {},
        });

        log.info("[RedeemCodeIntegration] Reward popup displayed successfully");
      } catch (error) {
        log.error(
          "[RedeemCodeIntegration] Failed to show reward popup:",
          error,
        );
        throw error;
      }
    },
    [],
  );

  const createMockRewardRecords = useCallback(
    async (rewards: Reward[], ownerId: string): Promise<void> => {
      try {
        log.info("[RedeemCodeIntegration] Creating mock reward records", {
          rewards,
          ownerId,
        });

        const mockRecords = rewards.map((reward) => ({
          orderId: reward.orderId,
          rewardType: reward.type,
          metadata: `${reward.name}-${reward.value}`,
          ownerId,
          createdAt: new Date().toISOString(),
        }));

        log.info("[RedeemCodeIntegration] Mock reward records created", {
          mockRecords,
        });

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        log.error(
          "[RedeemCodeIntegration] Failed to create reward records:",
          error,
        );
        throw error;
      }
    },
    [],
  );

  const refreshRewards = useCallback(async (): Promise<void> => {
    try {
      log.info("[RedeemCodeIntegration] Refreshing rewards query");

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_FEATURE_EQUITYLIST],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CREDENTIAL_CUSTOM_ME],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_FEATURE],
      });

      log.info("[RedeemCodeIntegration] Rewards query refreshed successfully");
    } catch (error) {
      log.error(
        "[RedeemCodeIntegration] Failed to refresh rewards query:",
        error,
      );
      throw error;
    }
  }, [queryClient]);

  return {
    triggerRewardPopup,
    createMockRewardRecords,
    refreshRewards,
  };
};
